import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Landmark } from "lucide-react";

const NIVEAU_COLORS = { A: "bg-green-600 text-white", B: "bg-emerald-400 text-emerald-950", C: "bg-amber-300 text-amber-950", D: "bg-gray-300 text-gray-700" };

export const SireneSearchDialog = ({ open, onClose, onImported }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState({ terme: "", codes_naf: "", departement: "", nombre: 10, campaign_id: "none" });
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) api.get("/campaigns").then(({ data }) => setCampaigns(data));
  }, [open]);

  const search = async () => {
    setBusy(true);
    setResults(null);
    try {
      const { data } = await api.post("/prospects/search-sirene", {
        terme: form.terme,
        codes_naf: form.codes_naf.split(",").map((c) => c.trim()).filter(Boolean),
        departement: form.departement,
        nombre: parseInt(form.nombre, 10) || 10,
        campaign_id: form.campaign_id === "none" ? null : form.campaign_id,
      });
      setResults(data);
      const sel = {};
      data.resultats.forEach((r, i) => {
        if (r.statut_import !== "rejete" && r.qualification.niveau !== "D") sel[i] = true;
      });
      setSelected(sel);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const importSelected = async () => {
    const rows = results.resultats.filter((_, i) => selected[i]).map((r) => r.data);
    if (!rows.length) { toast.error("Sélectionnez au moins une entreprise"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/prospects/search-sirene/import", {
        rows,
        campaign_id: form.campaign_id === "none" ? null : form.campaign_id,
      });
      toast.success(`${data.importes} entreprise(s) importée(s), ${data.rejetes} écartée(s)`);
      onImported?.();
      setResults(null);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="sirene-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Landmark size={18} /> Recherche automatique d'entreprises (SIRENE)</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">Source officielle : API Recherche d'Entreprises de l'État — aucune donnée inventée. Les e-mails et téléphones ne sont pas fournis par SIRENE : ils seront marqués « non trouvé ».</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Activité recherchée (mots-clés)</Label>
            <Input className="mt-1" value={form.terme} onChange={(e) => setForm({ ...form, terme: e.target.value })} placeholder="Ex : restauration collective" data-testid="sirene-term-input" />
          </div>
          <div>
            <Label>Codes NAF (séparés par des virgules)</Label>
            <Input className="mt-1" value={form.codes_naf} onChange={(e) => setForm({ ...form, codes_naf: e.target.value })} placeholder="Ex : 56.29A, 55.10Z" data-testid="sirene-naf-input" />
          </div>
          <div>
            <Label>Département</Label>
            <Input className="mt-1" value={form.departement} onChange={(e) => setForm({ ...form, departement: e.target.value })} placeholder="Ex : 69" data-testid="sirene-dept-input" />
          </div>
          <div>
            <Label>Nombre max.</Label>
            <Input type="number" min="1" max="50" className="mt-1" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="sirene-count-input" />
          </div>
          <div className="sm:col-span-2">
            <Label>Campagne (pour la qualification A/B/C/D)</Label>
            <Select value={form.campaign_id} onValueChange={(v) => setForm({ ...form, campaign_id: v })}>
              <SelectTrigger className="mt-1" data-testid="sirene-campaign-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucune (qualification C par défaut)</SelectItem>
                {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={search} disabled={busy} className="w-full" data-testid="sirene-search-button">
          <Search size={15} className="mr-1.5" /> {busy && !results ? "Recherche…" : "Rechercher"}
        </Button>

        {results && (
          <div className="space-y-2">
            <p className="text-sm font-medium">{results.total} entreprise(s) trouvée(s) — les niveaux D et rejets sont décochés (jamais contactés)</p>
            <div className="max-h-72 space-y-1.5 overflow-y-auto pr-1" data-testid="sirene-results">
              {results.resultats.map((r, i) => (
                <label key={i} className={`flex cursor-pointer items-start gap-2.5 rounded-md border p-2.5 text-sm ${r.statut_import === "rejete" || r.qualification.niveau === "D" ? "opacity-60" : ""}`}>
                  <Checkbox
                    checked={!!selected[i]}
                    disabled={r.statut_import === "rejete" || r.qualification.niveau === "D"}
                    onCheckedChange={(v) => setSelected({ ...selected, [i]: !!v })}
                    className="mt-0.5"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-medium">{r.data.raison_sociale}</span>
                      <Badge className={NIVEAU_COLORS[r.qualification.niveau]}>Niveau {r.qualification.niveau}</Badge>
                      {r.statut_import === "rejete" && <Badge variant="destructive">Rejeté</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {r.data.ville} ({r.data.departement}) · NAF {r.data.code_naf} · {r.data.taille_estimee ? `~${r.data.taille_estimee} sal.` : "taille : non trouvé"}
                      {r.data.contact_nom && ` · Dirigeant : ${r.data.contact_prenom} ${r.data.contact_nom}`}
                    </p>
                    <p className="text-xs text-muted-foreground">{[...r.raisons, ...r.qualification.raisons].join(" · ")}</p>
                  </div>
                </label>
              ))}
            </div>
            <Button onClick={importSelected} disabled={busy} className="w-full bg-accent hover:bg-accent/90" data-testid="sirene-import-button">
              Importer la sélection
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
